import React, { useState } from "react";
import { Modal, Button, Upload, Input, Tag, Space, Form, Spin } from "antd";
import {
  UploadOutlined,
  InboxOutlined,
  FileOutlined,
} from "@ant-design/icons";
import toast from "react-hot-toast";

const { Dragger } = Upload;
const { TextArea } = Input;

const DocumentUpload = ({ open, onClose, onUpload, loading = false }) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  const uploadProps = {
    name: "document",
    multiple: false,
    fileList,
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        toast.error("File must be smaller than 10MB!");
        return false;
      }

      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];

      if (!allowedTypes.includes(file.type)) {
        toast.error(
          "Invalid file type. Allowed: PDF, DOC, DOCX, XLS, XLSX, CSV, Images"
        );
        return false;
      }

      setFileList([file]);
      return false;
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (fileList.length === 0) {
        toast.error("Please select a file to upload");
        return;
      }

      setUploading(true);

      const formData = new FormData();
      formData.append("document", fileList[0]);
      formData.append("description", values.description || "");
      formData.append("tags", values.tags || "");

      await onUpload(formData);

      form.resetFields();
      setFileList([]);
      onClose();
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      title="Upload Document"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={uploading}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          onClick={handleSubmit}
          loading={uploading}
          disabled={fileList.length === 0}
        >
          Upload
        </Button>,
      ]}
      width={600}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item required>
          <Dragger {...uploadProps} className="p-4">
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ fontSize: 48 }} />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for a single file upload. PDF, DOC, DOCX, XLS, XLSX, CSV,
              Images (Max 10MB)
            </p>
          </Dragger>
        </Form.Item>

        {fileList.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <FileOutlined className="text-blue-500 text-xl" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {fileList[0].name}
                </p>
                <p className="text-xs text-gray-500">
                  {(fileList[0].size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
          </div>
        )}

        <Form.Item name="description" label="Description">
          <TextArea
            rows={3}
            placeholder="Add a description for this document (optional)"
          />
        </Form.Item>

        <Form.Item name="tags" label="Tags">
          <Input
            placeholder="Add tags separated by commas (e.g., invoice, report, q1-2024)"
            allowClear
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DocumentUpload;